import { createContext, Dispatch, ReactNode, SetStateAction, useCallback, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { PagesPaginateModel } from "../../models/Paginate/PagesPaginate";
import { ChatsHistoryService } from "../../services/Chats/ChatsHistoryService";
import { ChatModel } from "../../models/Chat/ChatModel";
import { ChatMessageModel } from "../../models/Chat/ChatMessageModel";

interface ContextProps {
    chat: ChatModel,
    setChat: Dispatch<SetStateAction<ChatModel>>,
    chats: ChatModel[],
    chatsMessages: ChatMessageModel[],
    params: any,
    setParams: Dispatch<SetStateAction<any>>,
    paramsMessages: any,
    setParamsMessages: Dispatch<SetStateAction<any>>,
    pages: PagesPaginateModel,
    setPages: Dispatch<SetStateAction<PagesPaginateModel>>,
    pagesMessages: PagesPaginateModel,
    setPagesMessages: Dispatch<SetStateAction<PagesPaginateModel>>,
    isLoading: boolean,
    isLoadingMessages: boolean,
    error: string,
    showModalMessageView: boolean,
    setShowModalMessageView: Dispatch<SetStateAction<boolean>>,
    selectedMessage: ChatMessageModel,
    setSelectedMessage: Dispatch<SetStateAction<ChatMessageModel>>,
    setError: Dispatch<SetStateAction<string>>,
    handleList: () => void,
    handleListMessages: () => void,
    handleDelete: (id: number) => Promise<boolean>,
}

export const ChatsHistoryContext = createContext<ContextProps>({} as ContextProps);

interface ChatsHistoryProviderProps {
    children: ReactNode;
}

export const ChatsHistoryProvider = ({ children }: ChatsHistoryProviderProps) => {
    const [chat, setChat] = useState<ChatModel>({} as ChatModel);
    const [chats, setChats] = useState<ChatModel[]>([]);
    const [chatsMessages, setChatsMessages] = useState<ChatModel[]>([]);
    const [params, setParams] = useState<any | null>(null);
    const [paramsMessages, setParamsMessages] = useState<any | null>(null);
    const [pages, setPages] = useState<PagesPaginateModel>({});
    const [pagesMessages, setPagesMessages] = useState<PagesPaginateModel>({});
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isLoadingMessages, setIsLoadingMessages] = useState<boolean>(true);
    const [error, setError] = useState<string>('');
    const [showModalMessageView, setShowModalMessageView] = useState<boolean>(false);
    const [selectedMessage, setSelectedMessage] = useState<ChatMessageModel>(new ChatMessageModel());

    const service = new ChatsHistoryService();
    const location = useLocation();

    const handleList = useCallback(async () => {
        if (!params || !location.pathname.includes('/chats/list')) {
            return false;
        }

        try {
            setIsLoading(true);
            const [_Response, _Error] = await service.list(params);
            setIsLoading(false);

            if (!!_Error) {
                return setError(_Response?.message || _Error);
            }

            setChats(_Response?.items);
            setPages(_Response?.pagination);
            return setError('');
        } catch (e) {
            setIsLoading(false);
            setError('Não foi possível carregar os chats.');
        }
    }, [params, location.pathname]);

    const handleListMessages = useCallback(async () => {
        if (!paramsMessages || !location.pathname.includes('/chats/list')) {
            return false;
        }

        try {
            setIsLoadingMessages(true);
            const [_Response, _Error] = await service.listMessages(paramsMessages);
            setIsLoadingMessages(false);

            if (!!_Error) {
                return setError(_Response?.message || _Error);
            }

            setChatsMessages(_Response?.items);
            setPagesMessages(_Response?.pagination);
            return setError('');
        } catch (e) {
            setIsLoadingMessages(false);
            setError('Não foi possível carregar as mensagens.');
        }
    }, [paramsMessages, location.pathname]);

    const handleDelete = async (id: number) => {
        try {
            setIsLoading(true);
            const [_Response, _Error] = await service.delete(id);
            setIsLoading(false);

            if (!!_Error) {
                setError(_Response?.message || _Error);
                return false;
            }

            setError('');
            return true;
        } catch (e: any) {
            setIsLoading(false);
            setError(e.response?.data?.message || 'Houve um erro ao deletar o chat');
            return false;
        }
    }

    useEffect(() => {
        !!error && toast.error(error);
    }, [error]);

    useEffect(() => {
        handleList().then();
    }, [handleList]);

    useEffect(() => {
        handleListMessages().then();
    }, [handleListMessages]);

    return (
        <ChatsHistoryContext.Provider value={{
            chat,
            setChat,
            chats,
            chatsMessages,
            params,
            setParams,
            paramsMessages,
            setParamsMessages,
            pages,
            setPages,
            pagesMessages,
            setPagesMessages,
            isLoading,
            isLoadingMessages,
            error,
            setError,
            showModalMessageView,
            setShowModalMessageView,
            selectedMessage,
            setSelectedMessage,
            handleList,
            handleListMessages,
            handleDelete
        }}>
            {children}
        </ChatsHistoryContext.Provider>
    );
}

export const useChatsHistory = () => useContext(ChatsHistoryContext);